import React from 'react';
import { Filter, ListContextData, ListProvider } from 'framework/list';
import useTimeline from './timelineHelper';
import type { TimelineEntity, TimelineItem, TimelineItemId, TimelineRegistry, TimelineResponseData } from './types';
import { ResourceData, ResourceId } from 'framework/crud';

export interface TimelineContextData extends ListContextData {
  isLoadingNextPage?: boolean;
  isLoadingPrevPage?: boolean;
  registries?: TimelineRegistry[];
  currentEditingId?: Nullable<StringOrNumber>;
  setCurrentEditingId?: (id: Nullable<StringOrNumber>) => void;
  updateInList?: (id: TimelineItemId, newEntity: TimelineEntity | TimelineItem) => void;
  wrapItem?: (item: TimelineItem, source: string) => TimelineEntity;
  addForm?: Nullable<React.ReactElement>;
  isHighlighted?: (arg: any) => boolean;
  isEntryViewMode?: boolean;
  pinTimelineAction?: (entityId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => Promise<any>;
  countPinElements?: Nullable<number>;
  useWindow?: boolean;
  scrollableContainer?: React.RefObject<HTMLElement>;
}

interface TimelineProps {
  getDataMethod: (request?: any) => Promise<TimelineResponseData>;
  registries: TimelineRegistry[];
  createTimelineItemMethod?: (itemType: string, data: ResourceData) => Nullable<Promise<TimelineItem>>;
  timelineEntryIdSource?: string;
  getPageByEntryIdMethod?: (id: StringOrNumber) => Promise<TimelineResponseData>;
  defaultFilter?: Filter;
  children?: React.ReactNode;
  pinTimelineAction?: (entityId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => Promise<any>;
  useWindow?: boolean;
  scrollableContainer?: React.RefObject<HTMLElement>;
}

/**
 * Component receives the data and set the context with needed methods
 * @param getDataMethod function that accepts search request and returns promise to response
 * @param registries array of items registries
 * @param createTimelineItemMethod - method that accepts timeline
 * @param timelineEntryIdSource - name of routing parameter where timeline entry id is located.
 * item type and request and sends request to backend.
 * @param getPageByEntryIdMethod - method that accepts id of the timeline entry and returns page with this entry.
 * @param children
 * @param defaultFilter
 */
const Timeline = ({
  getDataMethod,
  registries,
  createTimelineItemMethod,
  timelineEntryIdSource = 'timelineEntryId',
  getPageByEntryIdMethod = undefined,
  children,
  defaultFilter,
  pinTimelineAction,
  useWindow = true,
  scrollableContainer,
}: TimelineProps) => {
  const contextVariables = useTimeline({
    getDataMethod,
    registries,
    createTimelineItemMethod,
    timelineEntryIdSource,
    getPageByEntryIdMethod,
    defaultFilter,
    pinTimelineAction,
    useWindow,
    scrollableContainer,
  });

  return <ListProvider value={contextVariables}>{children}</ListProvider>;
};

export default Timeline;
